import { endpoints } from './endpoints';
import { httpClient } from './http-client';

/** The transactional-email templates the BE can render a preview for (matches BE EmailKind.key()). */
export type EmailPreviewKind =
  | 'admin-invite'
  | 'admin-password-reset'
  | 'root-transfer'
  | 'staff-invite'
  | 'staff-password-reset'
  // PRD 03 §3.8 (v1.3): withdrawal-result notice, one render per outcome. Auto-sent
  // by the BE on approve/reject; the admin page renders these previews only.
  | 'staff-withdrawal-approve'
  | 'staff-withdrawal-reject';

export interface EmailPreview {
  kind: string;
  subject: string;
  html: string;
}

/**
 * DISPLAY-only fields a preview may personalize with the values entered in the form, so a pre-send
 * preview matches the mail that will actually be sent. Mirrors the BE override whitelist — the server
 * only honors these keys and never lets a caller override a secret (temp password, invite/login link).
 */
export interface EmailPreviewVars {
  name?: string;
  role?: string;
  department?: string;
  jobRole?: string;
  // SHCT-651 — the envelope fields shown in the meta box at the top of every rendered
  // email (받는사람 / 발송일시). The FE used to paint its own meta box on top of the
  // server render to show these real values, which is what made the header appear
  // twice; it now sends them so the ONE server-rendered box carries them.
  // EmailPreviewController accepts and overrides both; a blank value falls back to its sample.
  recipientEmail?: string;
  sentAt?: string;
}

/**
 * Fetch the server-rendered email preview. The BE renders the body from its single owner catalog
 * (`messages_ko`); the page/modal displays THIS render, so the preview can never drift from
 * the mail that is actually sent. `resend` toggles the resend banner for invite kinds; `vars` passes
 * the entered display values (name/role/department/jobRole) so the preview greets the real recipient.
 * The HTML is server-produced and server-escaped at interpolation.
 */
export async function fetchEmailPreview(
  kind: EmailPreviewKind,
  resend = false,
  vars: EmailPreviewVars = {},
): Promise<EmailPreview> {
  const params: Record<string, string | boolean> = { resend };
  for (const [key, value] of Object.entries(vars)) {
    if (value != null && value !== '') params[key] = value;
  }
  const res = await httpClient.get<{ data: EmailPreview }>(
    `${endpoints.emailPreviews}/${kind}`,
    { params },
  );
  // Contract is `{ data: EmailPreview }`. A null/undefined body would crash the
  // preview pane reading `.subject` / `.html` off `undefined`; surface it as a
  // load error instead.
  if (res.data?.data == null) {
    throw new Error('미리보기를 불러올 수 없습니다.');
  }
  return res.data.data; // ApiSuccess<T> envelope = { data: T }
}
